import { useCallback, useMemo } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import {
  hasGallerySkuFilter,
  parseGallerySkuFilter,
} from 'utils/gallerySkuFilter';
import {
  parseCanvaAllProducts,
  parseCanvaCategoryId,
  withCanvaAllProducts,
  withCanvaCategory,
} from 'utils/canvaMatchView';
import { useCanvaPublishDraftId } from './useCanvaPublishDraft';

/**
 * Whether this gallery visit should show the match landing view, and how to leave it.
 *
 * Only a Canva visit has a match view at all: without a parked design there is nothing to match
 * against, so an ordinary gallery visit is untouched.
 */
export const useCanvaMatchView = (): {
  isMatchView: boolean;
  isCanvaPublish: boolean;
  /**
   * Gallery label the buyer is choosing a product from, once they have left the matches and
   * opened a category that holds more than one. Undefined everywhere else.
   */
  categoryId?: number;
  /**
   * The hand-off named the products itself, with {@code ?skuTags=} / {@code ?skuIds=}. The
   * Canva app sends those when the design's brand template resolves to more than one SKU, and
   * the buyer is choosing between exactly those -- so there is nothing for size matching to
   * decide, and narrowing the list by fit would hide products they were meant to see.
   */
  isTaggedPick: boolean;
  browseAllProducts: (options?: { replace?: boolean }) => void;
  openCategory: (categoryId: number) => void;
} => {
  const canvaDraftId = useCanvaPublishDraftId();
  const { search } = useLocation();
  const history = useHistory();

  const showingAllProducts = useMemo(() => parseCanvaAllProducts(search), [search]);
  const categoryId = useMemo(() => parseCanvaCategoryId(search), [search]);
  const taggedPick = useMemo(
    () => hasGallerySkuFilter(parseGallerySkuFilter(search)),
    [search]
  );

  /**
   * @param options.replace replaces the history entry instead of adding one. Use it when the
   *     switch is automatic: pushing would let Back return to a match view that has nothing to
   *     show, which would immediately send the buyer forward again.
   */
  const browseAllProducts = useCallback(
    (options?: { replace?: boolean }) => {
      const next = { search: withCanvaAllProducts(search) };
      if (options?.replace) {
        history.replace(next);
      } else {
        history.push(next);
      }
    },
    [history, search]
  );

  /**
   * Show one category's products so the buyer can say which of them the design goes on. Pushed,
   * not replaced: backing out of the choice should return to the categories.
   */
  const openCategory = useCallback(
    (nextCategoryId: number) => {
      history.push({ search: withCanvaCategory(search, nextCategoryId) });
    },
    [history, search]
  );

  return useMemo(
    () => ({
      // A category picked out of the full gallery is its own view, not the matches. Neither is
      // a hand-off that named the products: those are the products, matched or not.
      isMatchView:
        Boolean(canvaDraftId) && !showingAllProducts && !categoryId && !taggedPick,
      isCanvaPublish: Boolean(canvaDraftId),
      categoryId: canvaDraftId ? categoryId : undefined,
      isTaggedPick: Boolean(canvaDraftId) && taggedPick,
      browseAllProducts,
      openCategory,
    }),
    [
      canvaDraftId,
      showingAllProducts,
      categoryId,
      taggedPick,
      browseAllProducts,
      openCategory,
    ]
  );
};
